import { useEffect, useState } from "react"
import type { JSX } from "react"

import { useBrazo } from "../brazo.ts"
import { FILAS_POR_PAGINA_AL_ABRIR as FILAS_AL_ABRIR_DE_LA_PRIMITIVA, Paginado } from "../paginado.tsx"
import { selloDelBrazo } from "../zonas.ts"
import { segmentoParaAbrir } from "./cable.ts"
import { FilaDeCaso } from "./fila.tsx"
import { PanelDeConversacion } from "./panel.tsx"
import type { Atencion, Caso, Conversacion } from "./tipos.ts"

/**
 * Copia ANCHA de la constante compartida, y el por qué: en la primitiva
 * `FILAS_POR_PAGINA_AL_ABRIR` es el literal `10` (sale de `FILAS_POR_PAGINA`,
 * declarado `as const`) y `useState` NO ensancha los literales que vienen de una
 * `const` — sólo los literales frescos escritos en la llamada. Sin esta copia,
 * `useState(FILAS_POR_PAGINA_AL_ABRIR)` infiere el estado como `10` y el setter
 * deja de aceptar 50/100/200/500 (TS2322). El VALOR sigue saliendo de la
 * primitiva: acá no se escribe ningún literal.
 */
const FILAS_POR_PAGINA_AL_ABRIR: number = FILAS_AL_ABRIR_DE_LA_PRIMITIVA

/** El único aviso de red de la pantalla (CA-73 y CA-74). */
const AVISO_SIN_SERVIDOR = "No se pudo hablar con el servidor."

/**
 * El borde que la pantalla consume. Se inyecta para que la vista no sepa de
 * `fetch` ni de rutas: la pantalla pinta lo que el servidor manda (guard «la
 * vista pinta; el servidor decide»). El orden y la página los resuelve el
 * servidor; acá no hay `.sort` ni `.filter`.
 */
export interface BordeDeSituaciones {
  readonly listarCasos: (entrada: {
    readonly pagina: number
    readonly tamanoDePagina: number
  }) => Promise<{ readonly casos: readonly Caso[]; readonly total: number }>
  readonly leerConversacion: (entrada: {
    readonly idConversacion: string
    /**
     * La página pedida, **opcional a propósito** (CA-60). Al abrir el panel NO
     * se manda: el contrato dice que sin página el servidor devuelve la página
     * donde está la fila que abrió el caso (`contracts/conversation/index.ts:3825`,
     * `fake.ts:1486`). Sólo la manda el paginado, cuando la persona navega.
     */
    readonly pagina?: number
    readonly tamanoDePagina: number
  }) => Promise<Conversacion>
  readonly marcarAtendido: (id: string) => Promise<Atencion>
}

/**
 * La pantalla «Situaciones críticas» (CA-13, CA-14, CA-15, CA-17, regla 12).
 *
 * Por qué la pantalla no reordena ni filtra: el servidor decide el orden («del
 * más nuevo al más antiguo») y manda `total` y `pagina` ya resueltos. La vista
 * sólo pinta las filas que recibe.
 *
 * Por qué el paginado es la primitiva `Paginado`: las filas por página salen de
 * `FILAS_POR_PAGINA`, no de una lista escrita en la pantalla; así la pantalla no
 * puede divergir del resto de la consola.
 */
export function PantallaDeSituaciones(props: { readonly borde: BordeDeSituaciones }): JSX.Element {
  const brazo = useBrazo()
  const [casos, setCasos] = useState<readonly Caso[]>([])
  const [total, setTotal] = useState(0)
  const [pagina, setPagina] = useState(1)
  const [tamanoDePagina, setTamanoDePagina] = useState(FILAS_POR_PAGINA_AL_ABRIR)
  const [error, setError] = useState<string | null>(null)
  // Mientras la lista está en vuelo la tabla dice «Cargando…» en lugar de las
  // filas (regla 17, PRD:926). Sin este estado, una lista todavía vacía se
  // leería como «No hay casos para mostrar.», que es la regla 12 y miente.
  const [cargando, setCargando] = useState(true)
  const [casoAbierto, setCasoAbierto] = useState<Caso | null>(null)
  const [conversacion, setConversacion] = useState<Conversacion | null>(null)
  const [errorDeConversacion, setErrorDeConversacion] = useState<string | null>(null)
  // La página que PIDIÓ la persona en el panel. `null` = todavía no pidió
  // ninguna, y entonces el servidor decide: devuelve la página de la fila que
  // abrió el caso (CA-60). Arranca en `null` y vuelve a `null` al abrir otro
  // caso, para no arrastrar la página del anterior.
  const [paginaDelPanel, setPaginaDelPanel] = useState<number | null>(null)
  // Las filas por página del PANEL son suyas: no heredan las de la lista (CA-60:
  // con 50 en la lista el panel abre en 10 y la fila marcada cae en la página 2).
  // Vuelven a 10 al abrir otro caso, igual que la página.
  const [tamanoDelPanel, setTamanoDelPanel] = useState(FILAS_POR_PAGINA_AL_ABRIR)

  useEffect(() => {
    let vigente = true
    setCargando(true)
    props.borde.listarCasos({ pagina, tamanoDePagina }).then(
      (respuesta) => {
        if (!vigente) return
        setCasos(respuesta.casos)
        setTotal(respuesta.total)
        setError(null)
        setCargando(false)
      },
      () => {
        if (!vigente) return
        setError(AVISO_SIN_SERVIDOR)
        setCargando(false)
      },
    )
    return () => {
      vigente = false
    }
  }, [props.borde, pagina, tamanoDePagina, brazo])

  useEffect(() => {
    const aAbrir = casoAbierto === null ? null : segmentoParaAbrir(casoAbierto)
    if (aAbrir === null) {
      setConversacion(null)
      setErrorDeConversacion(null)
      return
    }
    let vigente = true
    setConversacion(null)
    setErrorDeConversacion(null)
    props.borde
      .leerConversacion({
        idConversacion: aAbrir,
        // Sin `pagina` en la apertura: el servidor trae la página de la fila
        // que abrió el caso (CA-60). La página sólo viaja cuando la persona ya
        // paginó; mientras `paginaDelPanel` sea `null` la clave no se manda, no
        // se manda `undefined`.
        ...(paginaDelPanel === null ? {} : { pagina: paginaDelPanel }),
        tamanoDePagina: tamanoDelPanel,
      })
      .then(
        (respuesta) => {
          if (vigente) setConversacion(respuesta)
        },
        () => {
          if (vigente) setErrorDeConversacion(AVISO_SIN_SERVIDOR)
        },
      )
    return () => {
      vigente = false
    }
  }, [props.borde, casoAbierto, paginaDelPanel, tamanoDelPanel])

  /*
   * Cuántas páginas hay: sale del total del servidor y del tamaño elegido. La
   * primitiva lo necesita para deshabilitar «Siguiente» en la última (CA-24).
   * Si el total baja y la página actual quedó más allá del final, se recorta acá
   * y no en la vista.
   */
  const paginas = Math.max(1, Math.ceil(total / tamanoDePagina))

  useEffect(() => {
    if (pagina > paginas) setPagina(paginas)
  }, [pagina, paginas])

  function alCambiarTamano(nuevo: number): void {
    setTamanoDePagina(nuevo)
    // Cambiar el tamaño de página devuelve a la primera: la página vieja puede
    // no existir con el tamaño nuevo (CA-23).
    setPagina(1)
  }

  return (
    <section className="situaciones-criticas">
      <header className="situaciones-criticas__encabezado">
        <h1 className="situaciones-criticas__titulo">Situaciones críticas</h1>
        <p className="situaciones-criticas__intro">
          Mensajes de participantes de{" "}
          {brazo === null ? "todos los brazos" : selloDelBrazo(brazo)} en los que el chatbot detectó
          un tema de contención, o de moderación marcado para revisión. Esta pantalla es solo para
          mirar: el contacto con el participante se hace fuera de la consola.
        </p>
        <p className="situaciones-criticas__leyenda">
          Todos los casos, del más nuevo al más antiguo. Fecha y hora en hora de Chile.
        </p>
      </header>
      <table className="situaciones-criticas__tabla">
        <thead>
          <tr>
            <th>Fecha y hora</th>
            <th>Conversación</th>
            <th>Categoría</th>
            <th>Participante</th>
            <th>Lo que escribió</th>
            <th>Lo que respondió el chatbot</th>
            <th>Estado</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {cargando ? (
            <tr>
              <td colSpan={8} className="situaciones-criticas__cargando">
                Cargando…
              </td>
            </tr>
          ) : error !== null ? (
            // El fallo de la lista va DENTRO de la tabla y NO cae en el vacío
            // (CA-73, regla 12): con el pedido caído, «No hay casos para mostrar.»
            // es falso —nunca llegó la lista— y el PRD (PRD_R4.md:929) los quiere
            // excluyentes. Antes se pintaban los dos a la vez.
            <tr>
              <td colSpan={8} className="situaciones-criticas__error">
                {AVISO_SIN_SERVIDOR}
              </td>
            </tr>
          ) : casos.length === 0 ? (
            <tr>
              <td colSpan={8} className="situaciones-criticas__vacio">
                No hay casos para mostrar.
              </td>
            </tr>
          ) : (
            casos.map((caso) => (
              <FilaDeCaso
                key={caso.id}
                caso={caso}
                onVerConversacion={() => {
                  // Cada caso abre en SU página: se descarta la que la persona
                  // hubiera elegido en el panel anterior (CA-60).
                  setPaginaDelPanel(null)
                  setTamanoDelPanel(FILAS_POR_PAGINA_AL_ABRIR)
                  setCasoAbierto(caso)
                }}
                onMarcar={props.borde.marcarAtendido}
              />
            ))
          )}
        </tbody>
      </table>
      <Paginado
        etiqueta="casos"
        pagina={pagina}
        total={total}
        tamanoDePagina={tamanoDePagina}
        unidad={{ singular: "caso", plural: "casos" }}
        onPagina={setPagina}
        onTamanoDePagina={alCambiarTamano}
      />
      {casoAbierto !== null && (
        <PanelDeConversacion
          caso={casoAbierto}
          conversacion={conversacion}
          error={errorDeConversacion}
          tamanoDePagina={tamanoDelPanel}
          alCerrar={() => setCasoAbierto(null)}
          onPagina={setPaginaDelPanel}
          onTamanoDePagina={(nuevo) => {
            setTamanoDelPanel(nuevo)
            // La página vieja puede no existir con el tamaño nuevo (como en la lista).
            setPaginaDelPanel(1)
          }}
          onMarcar={props.borde.marcarAtendido}
        />
      )}
    </section>
  )
}
